import { useEffect, useState } from 'react'

/**
 * Three-dot bouncing spinner — the canonical "we're loading something"
 * indicator across the app. Used at the auth boot screen, mid-session
 * uploads, and the results-fetch step. Always paired with an optional
 * label so the user knows what's happening, not just that something is.
 *
 * For waits longer than ~3s (e.g. the final scoring step), pass `phases`
 * instead of `label` and the component will rotate through the strings on
 * a fixed interval. This makes a 5–10s wait feel productive instead of
 * stuck — the user sees the system actively doing work.
 */

interface LoadingDotsProps {
  /** Static label shown beneath the dots. Mutually exclusive with `phases`. */
  label?: string
  /**
   * Sequence of progress messages to rotate through (e.g.
   * ["Transcribing…", "Analysing fluency…", "Scoring…"]). The final entry
   * stays on screen indefinitely once reached, so put your most accurate
   * "we're nearly there" line last.
   */
  phases?: string[]
  /** How long each phase is shown for, in ms. Defaults to 1800ms. */
  phaseIntervalMs?: number
  /** Optional second line — shorter, in the subtle text colour. */
  hint?: string
  /** Smaller dots for inline use (e.g. inside a button or a small card). */
  size?: 'sm' | 'md'
}

export function LoadingDots({
  label,
  phases,
  phaseIntervalMs = 1800,
  hint,
  size = 'md',
}: LoadingDotsProps) {
  const dotClass = size === 'sm' ? 'w-1.5 h-1.5' : 'w-2.5 h-2.5'
  const currentLabel = useCyclingLabel(phases, phaseIntervalMs) ?? label

  return (
    <div className="flex flex-col items-center gap-3 animate-fade-in">
      <div
        className="flex items-center gap-1.5"
        role="status"
        aria-label={currentLabel ?? 'Loading'}
        aria-live="polite"
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`${dotClass} rounded-full bg-brand-primary animate-bounce`}
            style={{ animationDelay: `${i * 120}ms` }}
          />
        ))}
      </div>
      {currentLabel && (
        <div
          // `min-h` keeps the layout from jumping by 20px when the label
          // appears or swaps to a longer/shorter phase string.
          className="text-brand-text-muted text-sm font-medium text-center min-h-[1.25rem] transition-opacity"
          // Re-key on label so React fades the new text in via the
          // animate-fade-in container above (cheap visual cue that
          // something just changed).
          key={currentLabel}
        >
          {currentLabel}
        </div>
      )}
      {hint && (
        <div className="text-brand-text-subtle text-xs text-center max-w-xs">
          {hint}
        </div>
      )}
    </div>
  )
}

/**
 * Cycles through `phases` on a fixed interval and stops on the last entry.
 * Returns `undefined` when `phases` is not provided so the caller can fall
 * back to a static `label`.
 *
 * Stopping on the last phase (rather than looping) is deliberate — looping
 * would say "Transcribing → Analysing → Scoring → Transcribing…" which
 * implies the system is going in circles. Holding the last message instead
 * communicates "we're on the final step, nearly done".
 */
function useCyclingLabel(
  phases: string[] | undefined,
  intervalMs: number,
): string | undefined {
  const [index, setIndex] = useState(0)

  useEffect(() => {
    if (!phases || phases.length <= 1) return
    setIndex(0)
    const id = setInterval(() => {
      setIndex((i) => (i < phases.length - 1 ? i + 1 : i))
    }, intervalMs)
    return () => clearInterval(id)
  }, [phases, intervalMs])

  if (!phases || phases.length === 0) return undefined
  return phases[index]
}
